import {
  callPeerName,
  callPeerNumber,
  useSoftphone
} from "@dialstack/sdk-react/softphone";

import { useAppSelector } from "app/hooks";
import { contactSubtitle } from "features/calls/rolodex/contact-kind.util";
import {
  type CallContact,
  useGetOutboundPatientMatchQuery
} from "services/dialstack";
import type { Patient } from "services/types/patients.type";

import { selectAttachmentOverlay } from "../call-attachments.slice";
import { popCallerName, popCallerNumber } from "../incoming-call-pop.slice";
import { isColleagueLine, lastTenDigits } from "../phone-match.util";
import {
  callerDisplayName,
  patientDisplayName
} from "../unknown-caller/caller-identity.util";
import useIncomingCallPatients from "../use-incoming-call-patients.hook";
import type { BarFactsInput } from "./bar-facts.util";
import useColleagueLookup, {
  colleagueLabel
} from "./use-colleague-lookup.hook";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";
import type { InboundCallMatch } from "./use-inbound-call-pops.hook";

type SoftphoneCall = ReturnType<typeof useSoftphone>["calls"][number];

export type CallIdentity = {
  // Who is on the line, the way every caller surface names them: the
  // patient, else the contact, else the caller-ID name, else the number.
  name: string;
  photo: string | null;
  initials: string;
  // The one patient this call is about, once there is exactly one.
  patient: Patient | null;
  contact: CallContact | null;
  // The chart link, when the caller is a patient.
  href: string | null;
  // The pieces of `barFacts` that describe who this is.
  caller: BarFactsInput["caller"];
  sharedPatientCount: number;
  transferredBy: string | null;
};

const initialsOf = (first?: string | null, last?: string | null) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.toUpperCase();

const patientName = (patient: Patient) =>
  patient.fullName ||
  `${patient.first_name ?? ""} ${patient.last_name ?? ""}`.trim();

// Who a live SDK call is with, for the folded bar (ENG-2226). Inbound calls
// take the backend's patient match from the websocket pop (`match`), plus any
// attach the front desk made since; outbound calls take the click-to-call
// patient or the backend's number match, as the outgoing card does.
export default function useCallIdentity(
  call: SoftphoneCall,
  match: InboundCallMatch | null
): CallIdentity {
  const { outboundCallPatients } = useDialstackSoftphone();
  const overlay = useAppSelector(selectAttachmentOverlay);
  const peerNumber = callPeerNumber(call);
  const number = match?.pop ? popCallerNumber(match.pop) : peerNumber;
  const fromName = match?.pop ? popCallerName(match.pop) : callPeerName(call);
  const tenDigits = lastTenDigits(number);

  const isOutbound = call.direction === "outbound";
  const clickPatient = isOutbound ? outboundCallPatients[tenDigits] : undefined;
  const { data: outboundMatch } = useGetOutboundPatientMatchQuery(
    { number: tenDigits },
    { skip: !isOutbound || clickPatient !== undefined || tenDigits.length < 10 }
  );

  const patientIds = isOutbound
    ? clickPatient
      ? [clickPatient.id]
      : (outboundMatch?.patient_ids ?? [])
    : (match?.pop?.patientIds ?? []);
  const { patients } = useIncomingCallPatients(patientIds);

  const backendCallId = match?.backendCallId ?? null;
  const attached = backendCallId !== null ? overlay[backendCallId] : undefined;
  const attachedPatient = attached?.patient
    ? (patients.find((patient) => patient.id === attached.patient?.id) ?? null)
    : null;
  const soloPatient =
    attachedPatient ?? (patients.length === 1 ? patients[0] : null);
  const sharedPatientCount =
    !attachedPatient && patients.length > 1 ? patients.length : 0;

  const contact =
    (attached ? attached.contact : null) ??
    match?.pop?.contact ??
    (isOutbound && !clickPatient ? (outboundMatch?.contact ?? null) : null);

  const lookupColleague = useColleagueLookup(
    isColleagueLine(number) ? [number] : []
  );
  const colleague = isColleagueLine(number)
    ? lookupColleague(number, fromName)
    : null;

  const transferredBy = match?.pop?.transfer
    ? (match.pop.transfer.byName ?? "a colleague")
    : null;

  if (soloPatient) {
    return {
      name: patientName(soloPatient),
      photo: soloPatient.photo || null,
      initials: initialsOf(soloPatient.first_name, soloPatient.last_name),
      patient: soloPatient,
      contact: null,
      href: `/patients/${soloPatient.id}`,
      caller: null,
      sharedPatientCount: 0,
      transferredBy
    };
  }
  if (sharedPatientCount > 0) {
    return {
      name: `${sharedPatientCount} patients`,
      photo: null,
      initials: String(sharedPatientCount),
      patient: null,
      contact: null,
      href: null,
      caller: null,
      sharedPatientCount,
      transferredBy
    };
  }
  // A patient attached by Quick add whose record has not been loaded into
  // this bar's query yet: name them from the overlay rather than the number.
  if (attached?.patient) {
    return {
      name: patientDisplayName(attached.patient),
      photo: null,
      initials: initialsOf(
        attached.patient.first_name,
        attached.patient.last_name
      ),
      patient: null,
      contact: null,
      href: `/patients/${attached.patient.id}`,
      caller: null,
      sharedPatientCount: 0,
      transferredBy
    };
  }
  if (contact) {
    return {
      name: contact.name,
      photo: null,
      initials: initialsOf(contact.name, null),
      patient: null,
      contact,
      href: null,
      caller: {
        kind: "contact",
        label: `Contact · ${contactSubtitle(contact)}`
      },
      sharedPatientCount: 0,
      transferredBy
    };
  }
  if (colleague) {
    return {
      name: colleagueLabel(colleague),
      photo: null,
      initials: initialsOf(colleague.name, null),
      patient: null,
      contact: null,
      href: null,
      caller: null,
      sharedPatientCount: 0,
      transferredBy
    };
  }
  const name = callerDisplayName({ fromName, number });
  return {
    name,
    photo: null,
    initials: initialsOf(name, null),
    patient: null,
    contact: null,
    href: null,
    caller: fromName?.trim() ? { kind: "caller-id" } : null,
    sharedPatientCount: 0,
    transferredBy
  };
}
